// ScadenzePage.jsx
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../contexts/auth';
import { fetchDocumentDeadlines, fetchContractDeadlines, fetchSignedDocumentDeadlines } from "../services/api";
import { listOrganizationalUnits } from '../services/api_org_unit';
import PopoverComponentCustom from "../components/popover_component";
import Footer from "../components/footer";
import CalendarDocuments from "../components/calendar_documents";
import { Link } from "wouter";
import dayjs from "dayjs";
import Fetching from '../components/fetching';


export default function ScadenzePage() {
  const { profile, token } = useContext(AuthContext);
  const isProfileComplete = profile.has_complete_profile;

  const [initialDate] = useState(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    const m = sp.get("month");
    if (!m) return null;

    const d = dayjs(m, "YYYY-MM-DD", true);
    return d.isValid() ? d.toDate() : null;
  });

  const [docs, setDocs] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [signedDocs, setSignedDocs] = useState([]);

  const [showDocs, setShowDocs] = useState(true);
  const [showContracts, setShowContracts] = useState(true);
  const [showSignedDocs, setShowSignedDocs] = useState(true);

  const [range, setRange] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingSignedDocs, setLoadingSignedDocs] = useState(false);

  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isUnitsLoading, setIsUnitsLoading] = useState(false);

  const selectedOuId =
    selectedUnit?.id && selectedUnit.id !== "__ALL__"
      ? selectedUnit.id
      : null;

  const loadingCalendar = (showDocs && loadingDocs) || (showContracts && loadingContracts) || (showSignedDocs && loadingSignedDocs);

  const docsCtrlRef = useRef(null);
  const ctrsCtrlRef = useRef(null);
  const signedDocsCtrlRef = useRef(null);

  const permissions = profile?.permissions || [];

  const canViewDocsDeadlines =
    profile?.customer_type !== "employee" ||
    profile?.employee?.permissions?.includes("hr.view_docs_ou") ||
    profile?.employee?.permissions?.includes("hr.view_docs_all");
  
  useEffect(() => {
    const fetchUnits = async () => {
      setIsUnitsLoading(true);

      try {
        const response = await listOrganizationalUnits(token, true, null, 'attendance');
        setUnits(response.data || []);
      } catch (error) {
        console.error("Errore caricamento OU:", error);
        setUnits([]);
      } finally {
        setIsUnitsLoading(false);
      }
    };

    if (token) {
      fetchUnits();
    } else {
      setIsUnitsLoading(false);
    }
  }, [token]);

  // carico DOCUMENTI nel range visibile (solo se abilitato)
  useEffect(() => {
    if (!token || !range || !showDocs || !canViewDocsDeadlines) {
      docsCtrlRef.current?.abort();
      setDocs([]);
      setLoadingDocs(false);
      return;
    }

    docsCtrlRef.current?.abort();
    const ctrl = new AbortController();
    docsCtrlRef.current = ctrl;

    const params = {
      date_from: range.start,
      date_to: range.end,
      ...(selectedOuId ? { ou: selectedOuId } : {}),
    };
    setLoadingDocs(true);

    fetchDocumentDeadlines(token, params, "payrolls", ctrl.signal)
      .then((data) => setDocs(data || []))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error(err);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoadingDocs(false);
      });

    return () => ctrl.abort();
  }, [token, range, showDocs, canViewDocsDeadlines, selectedOuId]);

  // Caricamento DOCUMENTI CON FIRMA nel range visibile
  useEffect(() => {
    if (!token || !range || !showSignedDocs  || !canViewDocsDeadlines) {
      signedDocsCtrlRef.current?.abort();
      setSignedDocs([]);
      setLoadingSignedDocs(false);
      return;
    }

    signedDocsCtrlRef.current?.abort();

    const ctrl = new AbortController();
    signedDocsCtrlRef.current = ctrl;

    const params = {
      date_from: range.start,
      date_to: range.end,
      ...(selectedOuId ? { ou: selectedOuId } : {}),
    };

    setLoadingSignedDocs(true);

    fetchSignedDocumentDeadlines(token, params, "payrolls", ctrl.signal)
      .then((data) => {
        if (!ctrl.signal.aborted) {
          setSignedDocs(
            Array.isArray(data)
              ? data
              : Array.isArray(data?.results)
                ? data.results
                : []
          );
        }
      })
      .catch((err) => {
        if (err?.name === "CanceledError" ||err?.code === "ERR_CANCELED") {
          return;
        }

        console.error("Errore caricamento documenti con firma:", err);

        if (!ctrl.signal.aborted) {
          setSignedDocs([]);
        }
      })
      .finally(() => {
        if (!ctrl.signal.aborted) {
          setLoadingSignedDocs(false);
        }
      });

    return () => ctrl.abort();
  }, [token, range, showSignedDocs, canViewDocsDeadlines, selectedOuId]);

  // carico CONTRATTI nel range visibile (solo se abilitato)
  useEffect(() => {
    if (!token || !range || !showContracts) {
      ctrsCtrlRef.current?.abort();
      setContracts([]);
      setLoadingContracts(false);
      return;
    }

    ctrsCtrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrsCtrlRef.current = ctrl;

    const params = {
      date_from: range.start,
      date_to: range.end,
      ...(selectedUnit?.id ? { ou: selectedUnit.id } : {}),
    };
    setLoadingContracts(true);

    fetchContractDeadlines(token, params, 'payrolls', ctrl.signal)
      .then((data) => setContracts(data || []))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error(err);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoadingContracts(false);
      });

    return () => ctrl.abort();
  }, [token, range, showContracts, selectedOuId]);

  // FullCalendar -> aggiorna range
  const handleDatesSet = useCallback(({ dateFrom, dateTo, startStr, endStr }) => {
    const start = dateFrom || dayjs(startStr).format("YYYY-MM-DD");
    const end = dateTo || dayjs(endStr).format("YYYY-MM-DD");

    setRange((prev) => {
      if (prev?.start === start && prev?.end === end) {
        return prev;
      }

      return { start, end };
    });
  }, []);

  // dopo patch documenti
  const handleDocUpdated = (updated) => {
    setDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
  };

  // dopo patch documenti con firma
  const handleSignedDocUpdated = (updated) => {
    setSignedDocs(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
  };

  // dopo patch contratti (end_date)
  const handleContractUpdated = (updated) => {
    setContracts(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
  };

  const handleSelectUnit = useCallback((unit) => {
    setSelectedUnit(unit || null);
  }, []);

  const handleResetUnit = useCallback(() => {
    setSelectedUnit(null);
  }, []);


  return (
    <div className='flex flex-col min-h-screen'>
      <PopoverComponentCustom title="Scadenze Documenti e Contratti" />

      {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email && (
        <div className="w-full">
          <div className="bg-orange-100 text-orange-600 p-4 mb-6">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo. <br />
              Compila i campi mancanti{' '}
              <Link
                to="/profilo/fatturazione"
                className="text-indigo-600 hover:text-indigo-800 underline"
              >
                cliccando qui
              </Link>
            </p>
          </div>
        </div>
      )}

      <main className="flex-grow pb-8">
        <div className='mx-auto w-full max-w-[1600px] px-4 md:px-6'>
          <div className="p-4 pt-8">
            <CalendarDocuments
              token={token}
              profile={profile}
              docs={showDocs ? docs : []}
              signedDocs={showSignedDocs ? signedDocs : []}
              contracts={showContracts ? contracts : []}
              onDocUpdated={handleDocUpdated}
              onSignedDocUpdated={handleSignedDocUpdated}
              onContractUpdated={handleContractUpdated}
              onDatesSet={handleDatesSet}
              isLoading={loadingCalendar}
              initialDate={initialDate}
              units={units}
              selectedUnit={selectedUnit}
              onSelectUnit={handleSelectUnit}
              onResetUnit={handleResetUnit}
              isUnitsLoading={isUnitsLoading}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}